/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license   MIT
 * @description Overhauled settings page with modular grid layout and improved UX.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Shield,
  LogOut,
  Key,
  VolumeX,
  Bell,
  BellOff,
  Disc,
  Link,
  Cpu,
  Save,
  Radio,
  Unlink,
  HelpCircle,
  Monitor,
  Smartphone,
  Trash2,
} from "lucide-react";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";
import { log } from "../utils/logger";

const SettingsCard = ({ title, icon: Icon, children, accentColor }) => {
  const accentMap = {
    blue: {
      border: "bg-blue-600",
      icon: "text-blue-500",
      hover: "hover:border-zinc-700",
    },
    indigo: {
      border: "bg-indigo-600",
      icon: "text-indigo-500",
      hover: "hover:border-zinc-700",
    },
    green: {
      border: "bg-green-600",
      icon: "text-green-500",
      hover: "hover:border-zinc-700",
    },
    red: {
      border: "bg-red-600",
      icon: "text-red-500",
      hover: "hover:border-zinc-700",
    },
  };

  const colors = accentMap[accentColor] || accentMap.blue;

  return (
    <div className="bg-zinc-950 border border-zinc-800 flex flex-col h-full relative group hover:border-zinc-700 transition-colors">
      <div
        className={`absolute top-0 left-0 w-1 h-full ${colors.border} opacity-50 group-hover:opacity-100 transition-opacity`}
      ></div>
      <div className="p-4 border-b border-zinc-900 bg-black flex items-center justify-between">
        <div className="text-sm font-black text-white uppercase italic flex items-center gap-2">
          <Icon size={16} className={colors.icon} />
          {title}
        </div>
      </div>
      <div className="p-6 flex-1 flex flex-col justify-center">{children}</div>
    </div>
  );
};

export default function Settings({
  onLogout,
  musicEnabled,
  onToggleMusic,
  sfxEnabled,
  onToggleSfx,
}) {
  const [sessions, setSessions] = useState([]);
  const [passwords, setPasswords] = useState({ current: "", new: "" });
  const [linkCode, setLinkCode] = useState("");
  const [discordLinked, setDiscordLinked] = useState(false);
  const [discordInfo, setDiscordInfo] = useState(null); // Added state for avatar/username
  const { addNotification } = useNotify();

  const fetchSessions = useCallback(async () => {
    try {
      const data = await callBackend("/auth/sessions");
      setSessions(data);
    } catch (err) {
      log("SETTINGS", "Failed to fetch active sessions");
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleRevokeSession = async (sessionId) => {
    try {
      await callBackend(`/auth/logout/${sessionId}`, { method: "POST" });
      addNotification("Session terminated.", "success");
      fetchSessions();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleLogoutAll = async () => {
    if (
      !window.confirm(
        "Terminate ALL other active sessions? This will kick you out of all other devices.",
      )
    )
      return;
    try {
      await callBackend("/auth/logout-all", { method: "POST" });
      addNotification("All other sessions purged.", "success");
      fetchSessions();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  // Define fetchUser in the component scope so it's accessible everywhere
  const fetchUser = useCallback(async () => {
    try {
      // Use /profile/me to get the discordUser metadata (avatar/username)
      const user = await callBackend("/profile/me");
      setDiscordLinked(!!user.discord_id);
      setDiscordInfo(user.discordUser);
    } catch (err) {
      log("SETTINGS", "Failed to load settings user data", err);
    }
  }, []);

  // Call fetchUser on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!passwords.current || !passwords.new) {
      addNotification("Please fill in both password fields.", "error");
      return;
    }

    try {
      const data = await callBackend("/auth/update-password", {
        method: "POST",
        body: {
          currentPassword: passwords.current,
          newPassword: passwords.new,
        },
      });
      addNotification(data.message, "success");
      setPasswords({ current: "", new: "" });
    } catch (err) {
      addNotification(err.message || "Connection failed.", "error");
      log("SETTINGS", "Failed to update password", err);
    }
  };

  const handleLinkDiscord = async () => {
    if (!linkCode) return;
    try {
      const data = await callBackend("/auth/link-discord", {
        method: "POST",
        body: { code: linkCode },
      });
      addNotification(data.message, "success");
      setLinkCode("");

      fetchUser();
    } catch (err) {
      addNotification(err.message, "error");
      log("SETTINGS", "Failed to link Discord", err);
    }
  };

  const handleUnlinkDiscord = async () => {
    if (
      !window.confirm("Are you sure you want to unlink your Discord account?")
    )
      return;

    try {
      const data = await callBackend("/auth/unlink-discord", {
        method: "POST",
      });
      addNotification(data.message, "success");
      setDiscordLinked(false);
      setDiscordInfo(null);
    } catch (err) {
      addNotification(err.message, "error");
      log("SETTINGS", "Failed to unlink Discord", err);
    }
  };

  return (
    <div className="max-w-4xl mx-auto font-mono animate-in fade-in duration-500 space-y-6">
      <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
        <div>
          <h2 className="text-3xl font-black text-white uppercase italic flex items-center gap-3">
            <Cpu className="text-zinc-600" size={32} />
            System Config
          </h2>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">
            User Preferences & Security Protocols
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Audio Module */}
        <SettingsCard title="Audio Environment" icon={Radio} accentColor="blue">
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-zinc-900/50 p-3 border border-zinc-800">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 ${musicEnabled ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-500"}`}
                >
                  {musicEnabled ? (
                    <Disc size={18} className="animate-spin-slow" />
                  ) : (
                    <VolumeX size={18} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-bold text-white uppercase">
                    Music
                  </p>
                  <p className="text-[9px] text-zinc-500 uppercase">
                    Atmosphere
                  </p>
                </div>
              </div>
              <button
                onClick={onToggleMusic}
                className={`w-12 h-6 rounded-full p-1 transition-colors ${musicEnabled ? "bg-blue-600" : "bg-zinc-800"}`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${musicEnabled ? "translate-x-6" : "translate-x-0"}`}
                />
              </button>
            </div>
            <div className="flex items-center justify-between bg-zinc-900/50 p-3 border border-zinc-800">
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 ${sfxEnabled ? "bg-green-600 text-white" : "bg-zinc-800 text-zinc-500"}`}
                >
                  {sfxEnabled ? (
                    <Bell size={18} className="animate-pulse" />
                  ) : (
                    <BellOff size={18} />
                  )}
                </div>
                <div>
                  <p className="text-xs font-bold text-white uppercase">SFX</p>
                  <p className="text-[9px] text-zinc-500 uppercase">Alerts</p>
                </div>
              </div>
              <button
                onClick={onToggleSfx}
                className={`w-12 h-6 rounded-full p-1 transition-colors ${sfxEnabled ? "bg-green-600" : "bg-zinc-800"}`}
              >
                <div
                  className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${sfxEnabled ? "translate-x-6" : "translate-x-0"}`}
                />
              </button>
            </div>
          </div>
        </SettingsCard>

        {/* Discord Module */}
        <SettingsCard
          title={
            <div className="flex items-center gap-2 relative group/help">
              External Link
              <HelpCircle
                size={14}
                className="text-zinc-600 cursor-help hover:text-indigo-400 transition-colors"
              />
              <div className="absolute left-0 bottom-full mb-2 hidden group-hover/help:block w-56 bg-zinc-900 border border-zinc-700 p-3 text-[10px] normal-case font-mono shadow-2xl z-50 leading-relaxed text-zinc-300">
                <p className="font-bold text-white mb-1 uppercase">
                  How to Link:
                </p>
                1. Join the official{" "}
                <span className="text-indigo-400">Trap Tycoon Discord</span>.
                <br />
                2. Type <span className="text-white">/link</span> in any
                channel.
                <br />
                3. Paste the code provided by the bot here.
              </div>
            </div>
          }
          icon={Link}
          accentColor="indigo"
        >
          {discordLinked ? (
            <div className="flex flex-col space-y-4">
              <div className="flex items-center gap-3 bg-zinc-900/50 p-3 border border-indigo-900/30">
                <img
                  src={
                    discordInfo?.avatar ||
                    "https://cdn.discordapp.com/embed/avatars/0.png"
                  }
                  alt="Avatar"
                  className="w-10 h-10 rounded-full border border-indigo-500/50 shadow-[0_0_10px_rgba(99,102,241,0.2)]"
                />
                <div className="overflow-hidden">
                  <p className="text-[10px] text-indigo-400 font-bold uppercase truncate">
                    Connected As
                  </p>
                  <p className="text-sm font-black text-white truncate">
                    {discordInfo?.username || "Discord User"}
                  </p>
                </div>
              </div>
              <button
                onClick={handleUnlinkDiscord}
                className="w-full bg-red-900/20 hover:bg-red-600 text-red-500 hover:text-white border border-red-900/50 hover:border-red-500 font-black py-2 transition-all uppercase italic text-xs flex items-center justify-center gap-2"
              >
                <Unlink size={16} /> Terminate Link
              </button>
            </div>
          ) : (
            <div className="flex gap-0">
              <div className="bg-zinc-900 border border-zinc-700 p-2 flex items-center justify-center">
                <Link size={16} className="text-zinc-500" />
              </div>
              <input
                placeholder="ENTER LINK CODE"
                className="flex-1 bg-black border-y border-r border-zinc-700 p-2 text-xs text-white outline-none focus:border-indigo-500 transition-colors uppercase placeholder:text-zinc-700"
                value={linkCode}
                onChange={(e) => setLinkCode(e.target.value)}
              />
              <button
                onClick={handleLinkDiscord}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 text-xs font-black uppercase italic transition-all border border-indigo-600 hover:border-indigo-400"
              >
                Connect
              </button>
            </div>
          )}
        </SettingsCard>

        <SettingsCard
          title="Active Access Points"
          icon={Monitor}
          accentColor="red"
        >
          <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
            {sessions.map((s) => (
              <div
                key={s.session_id}
                className="bg-zinc-900 border border-zinc-800 p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-zinc-800 text-zinc-400">
                    {s.user_agent.includes("Mobi") ? (
                      <Smartphone size={16} />
                    ) : (
                      <Monitor size={16} />
                    )}
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-[10px] font-bold text-white truncate w-32 md:w-48">
                      {s.ip_address}
                    </p>
                    <p className="text-[8px] text-zinc-500 uppercase">
                      Last Active:{" "}
                      {new Date(s.last_active_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>

                {/* Don't show delete button for current session (can use onLogout instead) */}
                {localStorage.getItem("token")?.includes(s.session_id) ? (
                  <span className="text-[8px] bg-green-900/30 text-green-500 px-2 py-1 font-black">
                    THIS DEVICE
                  </span>
                ) : (
                  <button
                    onClick={() => handleRevokeSession(s.session_id)}
                    className="text-red-500 hover:text-red-400 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleLogoutAll}
              className="w-full mt-4 text-[9px] text-zinc-500 hover:text-red-500 uppercase tracking-tighter border-t border-zinc-900 pt-4 transition-colors"
            >
              Force Global Logout (Purge All)
            </button>
          </div>
        </SettingsCard>

        {/* Security Module */}
        <SettingsCard
          title="Security Protocol"
          icon={Shield}
          accentColor="green"
        >
          <form onSubmit={handleUpdatePassword} className="space-y-3">
            <div className="relative group">
              <Key
                size={14}
                className="absolute left-3 top-3 text-zinc-600 group-focus-within:text-green-500 transition-colors"
              />
              <input
                type="password"
                placeholder="CURRENT PASSWORD"
                className="w-full bg-black border border-zinc-800 p-2 pl-9 text-xs text-white focus:border-green-500 outline-none transition-colors placeholder:text-zinc-700"
                value={passwords.current}
                onChange={(e) =>
                  setPasswords({ ...passwords, current: e.target.value })
                }
              />
            </div>
            <div className="relative group">
              <Key
                size={14}
                className="absolute left-3 top-3 text-zinc-600 group-focus-within:text-green-500 transition-colors"
              />
              <input
                type="password"
                placeholder="NEW PASSWORD"
                className="w-full bg-black border border-zinc-800 p-2 pl-9 text-xs text-white focus:border-green-500 outline-none transition-colors placeholder:text-zinc-700"
                value={passwords.new}
                onChange={(e) =>
                  setPasswords({ ...passwords, new: e.target.value })
                }
              />
            </div>
            <button className="w-full bg-zinc-800 hover:bg-green-600 hover:text-white text-zinc-400 font-black py-2 transition-all uppercase italic text-xs border border-zinc-800 hover:border-green-500 flex items-center justify-center gap-2">
              <Save size={14} /> Update Credentials
            </button>
          </form>
        </SettingsCard>

        {/* System Module */}
        <SettingsCard title="Session Control" icon={LogOut} accentColor="red">
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <div className="text-center">
              <p className="text-white font-bold text-sm uppercase">
                End Session?
              </p>
              <p className="text-zinc-500 text-[10px] uppercase">
                Any unsaved actions will be lost.
              </p>
            </div>
            <button
              onClick={onLogout}
              className="w-full bg-red-900/20 hover:bg-red-600 text-red-500 hover:text-white border border-red-900/50 hover:border-red-500 font-black py-3 transition-all uppercase italic text-xs flex items-center justify-center gap-2"
            >
              <LogOut size={16} /> Terminate Connection
            </button>
          </div>
        </SettingsCard>
      </div>
    </div>
  );
}
